import { Box, Stack, Typography, useTheme } from "@mui/material";
import {
  Button,
  SearchInput,
  PlusIcon,
  Drawer,
  Input,
  DeleteConfirmationModal,
} from "components";
import { HomeScreenLayout } from "components/layout/HomeScreenLayout";
// import { HomeScreenLayout } from "components";
import { DataTable } from "components/table/DataTable";
import { SizableTextField } from "library/base";
import { CustomTextField } from "library/system/custom-form";
import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { BillingTaxFields } from "./components/BillingTaxFields";
import { DrawerComponent } from "./components/DrawerComponent";
import {
  useDeleteTaxConfig,
  useGetAllTaxConfigs,
  useMainStore,
} from "controller";
import toast from "react-hot-toast";
import { useMixpanelPageViewTracker } from "utilz/helpers";
import { Decimal } from "utilz/decimal";

const BillingHeaders = [
  {
    name: "Tax Label",
    sortable: true,
    key: "tax_name",
  },
  {
    name: "Tax Rate",
    sortable: true,
    key: "tax_rate",
  },
  // {
  //   name: "Description",
  //   sortable: false,
  //   key: "description",
  // },
];

export const ServiceDeskBillingTax = (data: any) => {
  useMixpanelPageViewTracker({ event: "tax_page_visited" });
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>([]);
  const [mode, setMode] = useState<any>({
    method: "create",
    data: null,
    primaryButtonDisable: false,
  });
  const theme = useTheme();
  // const [perPageCount, setPerPageCount] = useState(10);
  const [pageNo, setPageNumber] = useState(1);
  const [sortColumn, setSortColumn] = useState("tax_name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [searchValue, setSearchValue] = useState("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [taxDataResult, setTaxDataResult] = useState<[]>([]);
  const perPageCount = useMainStore((state) => state.perPageCount);
  const setPerPageCount = useMainStore((state) => state.setPerPageCount);

  const {
    data: taxData,
    isFetching: isTaxDataLoading,
    refetch,
  } = useGetAllTaxConfigs({
    sortDirection,
    sortColumn,
    filter: [],
    pageNumber: pageNo,
    perPageCount: perPageCount.billingTax,
    // sortColumn: "created_at",
    // sortDirection: "desc",
    searchQuery: searchValue,
  });

  const { mutateAsync: deleteTaxConfig } = useDeleteTaxConfig();

  useEffect(() => {
    if (taxData) {
      setTaxDataResult(taxData?.result);
    }
  }, [taxData]);

  const openDrawer = (method: "create" | "update", id: string | null) => {
    // setIsDrawerOpen(true);
    if (method === "create") {
      setMode({ method: "create", data: null, primaryButtonDisable: false });
      setIsDrawerOpen(true);
    } else if (method === "update") {
      const findDataById: any = taxDataResult.find(
        (item: any) => item?.mapping_id === id,
      );
      setMode({
        method: "update",
        data: findDataById,
        primaryButtonDisable: findDataById?.meta?.is_default ? true : false,
      });
      setIsDrawerOpen(true);
    }
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
  };

  const handleItemClick = (id: string) => {
    const checkExistInTableData = taxDataResult.findIndex(
      (item: any) => item?.mapping_id === id,
    );
    if (checkExistInTableData !== -1) {
      const index = selectedRow.indexOf(id);
      // If ID is not in the selectedRow array, add it
      if (index === -1) {
        setSelectedRow([...selectedRow, id]);
      } else {
        // If ID is already in the selectedRow array, remove it
        const updatedSelectedRow = [...selectedRow];
        updatedSelectedRow.splice(index, 1);
        setSelectedRow(updatedSelectedRow);
      }
    }
  };

  const onHeaderCheckboxClick = (currentState: "checked" | "unchecked") => {
    // const selectedArray: any = [];
    if (currentState === "checked") {
      const selectedRowSet = new Set(selectedRow);
      const productIds = taxDataResult
        .filter(
          (item: any) =>
            !selectedRowSet.has(item.mapping_id) && !item.meta?.is_default,
        )
        .map((item: any) => item.mapping_id);
      setSelectedRow(productIds);
    } else {
      setSelectedRow([]);
    }
  };

  const handleDeleteClick = async () => {
    try {
      // await deleteSelectedCustomers({ customerIds: selectedCustomers });
      await deleteTaxConfig({ tax_ids: selectedRow });
      setIsDeleteModalOpen(false);
      setSelectedRow([]);
      toast.success("Tax successfully deleted");
    } catch (error) {
      toast.error("Please try again later");
    }
  };

  const cellBuilders = {
    // tax_rate: (value: any) => `${(value?.tax_rate / 100).toFixed(2)}%`,
    tax_rate: (value: any) =>
      `${new Decimal(value?.tax_rate).div(100).toNumber()}%`,
  };

  const checkIsCheckboxDisabled = (rowData: any) => {
    return rowData?.meta?.is_default === true;
  };

  const handleSortClick = (column: string) => {
    if (column === sortColumn) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const componentName = mode?.method === "update" ? "Edit Tax" : "Add Tax";

  return (
    <HomeScreenLayout
      title="Taxes"
      rightActionComponent={
        <Stack direction={"row"} gap={"24px"}>
          {/* search input */}
          <SearchInput
            name="search"
            placeholder="Search"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />
          <Button
            endIcon={<PlusIcon />}
            onClick={() => openDrawer("create", null)}
          >
            Tax
          </Button>
        </Stack>
      }
    >
      {isDrawerOpen && (
        <DrawerComponent
          {...{ isDrawerOpen, closeDrawer, componentName, mode }}
        />
      )}
      <Box sx={{ paddingX: "32px" }}>
        <DataTable
          showPagination={true}
          headers={BillingHeaders}
          data={isTaxDataLoading ? [] : taxDataResult || []}
          selectedItems={selectedRow}
          totalItems={taxData?.totalCount}
          totalPages={taxData?.totalPages}
          cellBuilders={cellBuilders}
          isCheckboxDisabled={checkIsCheckboxDisabled}
          onSortingClick={(column) => handleSortClick(column)}
          sortProps={{
            activeColumn: sortColumn,
            activeDirection: sortDirection,
          }}
          currentPageNumber={pageNo}
          onItemClick={handleItemClick} //check box
          onAllSelect={(currentState: any) => {
            onHeaderCheckboxClick(currentState);
          }}
          isDataLoading={isTaxDataLoading}
          itemsPerPage={perPageCount.billingTax}
          onPerPageChange={(itemsPerPage) => {
            setPerPageCount({ type: "billingTax", count: itemsPerPage });
          }}
          onPagination={(d) => {
            setPageNumber(d);
          }}
          multiselect={true}
          handleRowClick={(id: any) => {
            openDrawer("update", id?.mapping_id);
          }}
          actionContent={
            <Stack direction={"row"} gap={"24px"}>
              <Button
                onClick={() => setIsDeleteModalOpen(true)}
                variant="primary"
                sx={{
                  bgcolor: theme.palette.error.main,
                  "&:hover": { backgroundColor: "#A9050E" },
                }}
              >
                Delete
              </Button>
            </Stack>
          }
          // isCheckboxDisabled={(item: any) => {
          //   return false;
          // }}
        />
        {isDeleteModalOpen && (
          <DeleteConfirmationModal
            open={isDeleteModalOpen}
            onPrimaryButtonClick={handleDeleteClick}
            // onPrimaryButtonClick={() => {}}
            onSecondaryButtonClick={() => setIsDeleteModalOpen(false)}
            title="Delete Tax configuration"
            tagline="Are you sure you want to delete?"
            // tagline={`Are you sure you want to delete ${
            //   selectedCustomers.length
            // } ${selectedCustomers.length > 1 ? "customers" : "customer"}?`}
          />
        )}
      </Box>
    </HomeScreenLayout>
  );
};
